import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: EmblaCarouselStyles;
    children?: React.ReactNode;
}

type Props = OwnProps;

export interface EmblaCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

export const EmblaCarouselStyles: EmblaCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
            &:disabled {
                cursor: not-allowed;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 92%;
                        max-width: 92%;
                    `,
                    css`
                        flex-basis: 96%;
                        max-width: 96%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%; /* mobile: 1 product per view */
                    `,
                    css`
                        width: 50%; /* tablet: 2 per view */
                    `,
                    css`
                        width: 25%; /* desktop: 4 per view */
                    `,
                    css`
                        width: 25%;
                    `,
                    css`
                        width: 25%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 4%;
                        max-width: 4%;
                    `,
                    css`
                        flex-basis: 2%;
                        max-width: 2%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 5px;
            background: #f8f8f8;
            z-index: 1;
            &:hover {
                background: ${getColor("primary")};
                opacity: 0.8;
                span {
                    color: #fff !important;
                }
            }
            &:disabled {
                cursor: not-allowed;
            }
        `,
    },
};

const getVisibleSlides = () => {
    if (typeof window === "undefined") {
        // Default to desktop on SSR
        return 4;
    }
    const width = window.innerWidth;
    if (width < 480) {
        return 1;
    } // mobile
    if (width < 768) {
        return 2;
    } // tablet
    return 4; // desktop
};

const ProductDetailsCarousel = ({ extendedStyles, children }: Props) => {
    const [styles] = useState(() => mergeToNew(EmblaCarouselStyles, extendedStyles));
    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [visibleSlides, setVisibleSlides] = useState(getVisibleSlides());

    const childrenCount = React.Children.count(children);
    const shouldEnableScroll = childrenCount > visibleSlides;

    const setCanScroll = () => {
        if (!embla) {
            return;
        }
        // If not enough slides to scroll, disable arrows
        if (childrenCount <= visibleSlides) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
    };

    useEffect(() => {
        const handleResize = () => setVisibleSlides(getVisibleSlides());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            loop: shouldEnableScroll,
        });

        setCanScroll();
    }, [embla, shouldEnableScroll, visibleSlides, childrenCount]);

    return (
        <GridContainer {...styles.mainContainer}>
            <GridItem {...styles.prevArrowGridItem}>
                <Button
                    {...styles.prevArrowButton}
                    title="Previous"
                    onClick={() => embla?.scrollPrev()}
                    disabled={!canScrollPrev || childrenCount <= visibleSlides}
                >
                    <ButtonIcon src={ChevronLeft} />
                </Button>
            </GridItem>
            <GridItem {...styles.carouselGridItem}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer}>{children}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.nextArrowGridItem}>
                <Button
                    {...styles.nextArrowButton}
                    title="Next"
                    onClick={() => embla?.scrollNext()}
                    disabled={!canScrollNext || childrenCount <= visibleSlides}
                >
                    <ButtonIcon src={ChevronRight} />
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default ProductDetailsCarousel;
